import { z } from 'zod';

/**
 * The icon of a product's strength (adr/0048). The engine picks one per strength from this list.
 * - SPEED: time, output, a fast job
 * - CLEAN: cleaning, hygiene
 * - SHIELD: durability, safety, reliability
 * - SAVINGS: saving money, energy or raw material
 * - QUALITY: a good result, quality of the product
 * - CAPACITY: volume, quantity, load
 * - VARIETY: many options, sizes, uses
 * - POWER: power, performance
 * - COLD: cooling, freezing
 * - HEAT: cooking, baking, heating
 * - PRECISION: accuracy, control, uniform results
 * - EASY: simple operation, comfort
 * - SPACE: size, saving space, mobility
 * - SERVICE: maintenance, parts, a long life
 */
export const FLYER_ICONS = [
  'SPEED',
  'CLEAN',
  'SHIELD',
  'SAVINGS',
  'QUALITY',
  'CAPACITY',
  'VARIETY',
  'POWER',
  'COLD',
  'HEAT',
  'PRECISION',
  'EASY',
  'SPACE',
  'SERVICE',
] as const;
export const flyerIconSchema = z.enum(FLYER_ICONS);
export type FlyerIcon = z.infer<typeof flyerIconSchema>;

/**
 * The longest strength of a product the engine may write, and how many (adr/0048). A product's
 * strengths are written with its copy, from the original site's strengths of the product, and
 * shown on the store's product page and its flyer.
 */
export const PRODUCT_FEATURE_LIMITS = { title: 60, text: 180, min: 3, max: 7 } as const;

export const productFeatureSchema = z.object({
  icon: flyerIconSchema,
  /** A short headline, a few words. */
  title: z.string().min(1).max(PRODUCT_FEATURE_LIMITS.title),
  /** One sentence: what the strength gives the business. */
  text: z.string().min(1).max(PRODUCT_FEATURE_LIMITS.text),
});
export type ProductFeature = z.infer<typeof productFeatureSchema>;

/**
 * The product's "ideal for" lines and its care and cleaning instructions (adr/0054), as the
 * original site's product page has them: written for each store with its copy, from the original
 * site's lines of the product, and shown on the store's product page and its PDF sheet. A line
 * is one business type or one instruction; the longest and how many the engine may write.
 */
export const PRODUCT_LIST_LIMITS = {
  idealFor: { line: 80, max: 8 },
  care: { line: 200, max: 8 },
} as const;

/**
 * The lists of a product's page that its store may edit (adr/0055): the strengths, the "ideal
 * for" lines and the care instructions. The engine writes them with the copy; a list the store
 * edited by hand is the store's from then on, and no rewrite of the engine replaces it (not the
 * daily catalog update, not a new version of the copy) until the store gives it back.
 */
export const PRODUCT_TEXT_LISTS = ['features', 'idealFor', 'care'] as const;
export const productTextListSchema = z.enum(PRODUCT_TEXT_LISTS);
export type ProductTextList = z.infer<typeof productTextListSchema>;

const listLine = (max: number) => z.string().trim().min(1).max(max);

/**
 * The lists as the store saves them. No least number: a store may leave a list empty, and the
 * product page then has no such section.
 */
export const productListsInputSchema = z.object({
  features: z
    .array(
      productFeatureSchema.extend({
        title: listLine(PRODUCT_FEATURE_LIMITS.title),
        text: listLine(PRODUCT_FEATURE_LIMITS.text),
      }),
    )
    .max(PRODUCT_FEATURE_LIMITS.max),
  idealFor: z
    .array(listLine(PRODUCT_LIST_LIMITS.idealFor.line))
    .max(PRODUCT_LIST_LIMITS.idealFor.max),
  care: z.array(listLine(PRODUCT_LIST_LIMITS.care.line)).max(PRODUCT_LIST_LIMITS.care.max),
});
